import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '@/lib/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertTriangle, User, Building2, Check } from 'lucide-react';
import { trackEvent } from '@/components/SEOHead';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const checks = [
    { ok: password.length >= 8, label: 'At least 8 characters' },
    { ok: /[A-Z]/.test(password), label: 'One uppercase letter' },
    { ok: /[0-9]/.test(password), label: 'One number' },
    { ok: /[^A-Za-z0-9]/.test(password), label: 'One special character' },
  ];

  const allChecksPassed = checks.every(c => c.ok);
  const canSubmit = allChecksPassed && agreedToTerms && name && email && companyName;

  const disabledReason = !name ? 'Enter your full name'
    : !email ? 'Enter your work email'
    : !companyName ? 'Enter your company name'
    : !allChecksPassed ? 'Password must meet all requirements'
    : !agreedToTerms ? 'Accept the terms and privacy policy'
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    trackEvent('sign_up_started');
    try {
      await register(name, email, password, companyName, agreedToTerms);
      trackEvent('sign_up_completed');
      navigate('/onboarding');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2533] flex items-center justify-center px-4" data-testid="register-page">
      <main id="main-content" className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-white font-bold text-2xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold text-2xl">TradePath</span>
          </div>
          <p className="text-[#B0BEC5] text-sm">Supply Chain Intelligence Platform</p>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <h1 className="text-xl font-bold text-[#0A2533] mb-1" data-testid="register-title">Create your account</h1>
          <p className="text-sm text-[#4E6974] mb-6">Get started with supply chain risk intelligence</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4" role="alert" data-testid="register-error">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="register-name" className="block text-sm font-medium text-[#4E6974] mb-1.5">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input id="register-name" data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="Jane Smith" required autoComplete="name" />
              </div>
            </div>

            <div>
              <label htmlFor="register-email" className="block text-sm font-medium text-[#4E6974] mb-1.5">Work email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input id="register-email" data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="jane@company.com" required autoComplete="email" />
              </div>
            </div>

            <div>
              <label htmlFor="register-company" className="block text-sm font-medium text-[#4E6974] mb-1.5">Organisation name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input id="register-company" data-testid="register-company" type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="Acme Trading Co" required />
              </div>
            </div>

            <div>
              <label htmlFor="register-password" className="block text-sm font-medium text-[#4E6974] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" aria-hidden="true" />
                <input id="register-password" data-testid="register-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] placeholder:text-[#B0BEC5] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none transition-colors"
                  placeholder="Create a password" required autoComplete="new-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B0BEC5] hover:text-[#4E6974]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="register-toggle-password">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-x-3 mt-2" aria-live="polite" aria-label="Password requirements">
                {checks.map((c, i) => (
                  <div key={i} className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-[#2E7D32]' : 'text-[#B0BEC5]'}`}>
                    <Check className="h-3 w-3" aria-hidden="true" /> {c.label}
                  </div>
                ))}
              </div>
            </div>

            <label htmlFor="register-consent" className="flex items-start gap-2.5 cursor-pointer" data-testid="register-consent-label">
              <input id="register-consent" type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[#E0E0E0] text-[#0097A7] focus:ring-[#0097A7] cursor-pointer"
                data-testid="register-consent-checkbox" />
              <span className="text-xs text-[#4E6974] leading-relaxed">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener" className="text-[#0097A7] hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener" className="text-[#0097A7] hover:underline">Privacy Policy</a>.
              </span>
            </label>

            <button data-testid="register-submit" type="submit"
              disabled={loading || !canSubmit}
              aria-disabled={!canSubmit && !loading ? 'true' : undefined}
              title={disabledReason || undefined}
              className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white font-medium text-sm rounded-lg transition-colors duration-200">
              {loading ? 'Creating account...' : 'Create account'}
            </button>
            {!canSubmit && !loading && disabledReason && (
              <p className="text-xs text-[#4E6974] text-center" aria-live="polite">{disabledReason}</p>
            )}
          </form>

          <p className="text-center text-sm text-[#4E6974] mt-5">
            Already have an account?{' '}
            <Link to="/signin" className="text-[#0097A7] hover:underline font-medium" data-testid="register-signin-link">Sign in</Link>
          </p>
        </div>

        <p className="text-center text-[#4E6974] text-xs mt-6">
          Protected by Nuvanta security protocols
        </p>
      </main>
    </div>
  );
}
